import { ZERO_DECIMAL_CURRENCIES } from 'constants/payment'
import { floatify } from 'lib/maths/mathUtils'

export const SPLIT_CARD_MIN_COUNT = 2
export const SPLIT_CARD_MAX_COUNT = 5

export interface SplitCardLeg {
  id: string
  amount: string
}

export interface InlineSplitCardLeg extends SplitCardLeg {
  checkoutPaymentId: string
  source: string
}

export type SplitPaymentBlockReason =
  | 'needs_another_card'
  | 'remaining'
  | 'over_allocated'

export function getCurrencyFractionDigits(currencyCode: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode.toUpperCase()) ? 0 : 2
}

function getMinorUnitFactor(currencyCode: string): number {
  return 10 ** getCurrencyFractionDigits(currencyCode)
}

function getMoneyInputPattern(currencyCode: string | undefined, requireInteger: boolean): RegExp {
  const integerPattern = requireInteger ? '\\d+' : '\\d*'
  const fractionDigits = currencyCode ? getCurrencyFractionDigits(currencyCode) : 2
  if (fractionDigits === 0) return new RegExp(`^${integerPattern}$`)
  return new RegExp(`^${integerPattern}(?:\\.\\d{0,${fractionDigits}})?$`)
}

export function isMoneyInputWithinCurrencyPrecision(value: string, currencyCode: string): boolean {
  return getMoneyInputPattern(currencyCode, false).test(value)
}

export function hasMoneyInputExcessPrecision(value: string, currencyCode: string): boolean {
  const fractionDigits = getCurrencyFractionDigits(currencyCode)
  const fraction = value.trim().match(/^\d+\.(\d+)$/)?.[1]
  return fraction !== undefined && fraction.length > fractionDigits
}

export function parseMoneyInput(value: string, currencyCode?: string): number | undefined {
  const trimmedValue = value.trim()
  if (!trimmedValue || !getMoneyInputPattern(currencyCode, true).test(trimmedValue)) return undefined
  const amount = Number(trimmedValue)
  if (!Number.isFinite(amount) || amount < 0) return undefined
  return floatify(amount)
}

export function moneyToMinorUnits(amount: number, currencyCode: string): number {
  return Math.round(amount * getMinorUnitFactor(currencyCode))
}

export function minorUnitsToMoney(amount: number, currencyCode: string): number {
  return amount / getMinorUnitFactor(currencyCode)
}

export function moneyToCents(amount: number): number {
  return Math.round(amount * 100)
}

export function getLegAmountCents(amount: string, fallbackAmount: number, currencyCode = 'AUD'): number {
  const parsed = parseMoneyInput(amount, currencyCode)
  return moneyToMinorUnits(parsed !== undefined && parsed > 0 ? parsed : fallbackAmount, currencyCode)
}

export function formatMoneyInput(amount: number, currencyCode: string): string {
  return amount.toFixed(getCurrencyFractionDigits(currencyCode))
}

export function createInitialSplitCardLegs(): Array<SplitCardLeg> {
  return [
    { id: 'card-1', amount: '' },
    { id: 'card-2', amount: '' },
  ]
}

export function getActiveSplitCardLegs<T extends SplitCardLeg>(legs: Array<T>, currencyCode?: string): Array<T> {
  return legs.filter(leg => {
    const amount = parseMoneyInput(leg.amount, currencyCode)
    return amount !== undefined && amount > 0
  })
}

export function getAllocatedMinorUnits(legs: Array<SplitCardLeg>, currencyCode: string): number {
  return legs.reduce((total, leg) => {
    const amount = parseMoneyInput(leg.amount, currencyCode)
    if (amount === undefined) return total
    return total + moneyToMinorUnits(amount, currencyCode)
  }, 0)
}

export function getRemainingMinorUnits(totalAmount: number, legs: Array<SplitCardLeg>, currencyCode: string): number {
  return moneyToMinorUnits(totalAmount, currencyCode) - getAllocatedMinorUnits(legs, currencyCode)
}

export function getSplitPaymentBlockReason(
  totalAmount: number,
  legs: Array<SplitCardLeg>,
  currencyCode: string,
): SplitPaymentBlockReason | undefined {
  const remainingMinorUnits = getRemainingMinorUnits(totalAmount, legs, currencyCode)
  if (remainingMinorUnits < 0) return 'over_allocated'
  if (getActiveSplitCardLegs(legs, currencyCode).length < SPLIT_CARD_MIN_COUNT) return 'needs_another_card'
  if (remainingMinorUnits > 0) return 'remaining'
  return undefined
}

export function canAddSplitCardLeg(legs: Array<SplitCardLeg>): boolean {
  return legs.length < SPLIT_CARD_MAX_COUNT
}

export function scaleSplitAmountsToTotal<T extends SplitCardLeg>(
  legs: Array<T>,
  totalAmount: number,
  currencyCode: string,
): Array<T> {
  const targetMinorUnits = Math.max(0, moneyToMinorUnits(totalAmount, currencyCode))
  const allocatedMinorUnits = getAllocatedMinorUnits(legs, currencyCode)
  if (allocatedMinorUnits === 0 || allocatedMinorUnits === targetMinorUnits) return legs

  const legMinorUnits = legs.map(leg => {
    const amount = parseMoneyInput(leg.amount, currencyCode)
    return amount === undefined ? undefined : moneyToMinorUnits(amount, currencyCode)
  })

  if (targetMinorUnits === 0) {
    let changed = false
    const next = legs.map((leg, index) => {
      if (!legMinorUnits[index]) return leg
      changed = true
      return { ...leg, amount: formatMoneyInput(0, currencyCode) }
    })
    return changed ? next : legs
  }

  const shares = legMinorUnits.map(minorUnits => {
    if (minorUnits === undefined) return undefined
    if (minorUnits <= 0) return 0
    return Math.floor((minorUnits * targetMinorUnits) / allocatedMinorUnits)
  })
  let leftover = targetMinorUnits - shares.reduce<number>((sum, minorUnits) => sum + (minorUnits ?? 0), 0)
  const scaled = shares.map((share, index) => {
    if (share === undefined || leftover <= 0 || (legMinorUnits[index] ?? 0) <= 0) return share
    leftover -= 1
    return share + 1
  })

  let changed = false
  const next = legs.map((leg, index) => {
    const nextMinorUnits = scaled[index]
    if (nextMinorUnits === undefined || nextMinorUnits === legMinorUnits[index]) return leg
    changed = true
    return { ...leg, amount: formatMoneyInput(minorUnitsToMoney(nextMinorUnits, currencyCode), currencyCode) }
  })
  return changed ? next : legs
}

export function capSplitAmountsToTotal<T extends SplitCardLeg>(
  legs: Array<T>,
  totalAmount: number,
  currencyCode: string,
): Array<T> {
  if (getAllocatedMinorUnits(legs, currencyCode) <= Math.max(0, moneyToMinorUnits(totalAmount, currencyCode))) return legs
  return scaleSplitAmountsToTotal(legs, totalAmount, currencyCode)
}

export function adjustSplitAmountsForTotalChange<T extends SplitCardLeg>(
  legs: Array<T>,
  previousTotal: number,
  nextTotal: number,
  currencyCode: string,
): Array<T> {
  if (moneyToMinorUnits(previousTotal, currencyCode) === moneyToMinorUnits(nextTotal, currencyCode)) return legs

  const allocatedMinorUnits = getAllocatedMinorUnits(legs, currencyCode)
  if (allocatedMinorUnits > 0 && allocatedMinorUnits === moneyToMinorUnits(previousTotal, currencyCode)) {
    return scaleSplitAmountsToTotal(legs, nextTotal, currencyCode)
  }
  return legs
}

export function getMaxAllocatableMinorUnits(
  totalAmount: number,
  legs: Array<SplitCardLeg>,
  legId: string,
  currencyCode: string,
): number {
  const otherMinorUnits = getAllocatedMinorUnits(legs.filter(leg => leg.id !== legId), currencyCode)
  return Math.max(0, moneyToMinorUnits(totalAmount, currencyCode) - otherMinorUnits)
}

export function splitMoneyEvenly(amount: number, parts: number, currencyCode: string): Array<number> {
  if (parts <= 0) return []
  const totalMinorUnits = Math.max(0, moneyToMinorUnits(amount, currencyCode))
  const baseMinorUnits = Math.floor(totalMinorUnits / parts)
  const remainder = totalMinorUnits % parts
  return Array.from({ length: parts }, (_, index) => (
    minorUnitsToMoney(baseMinorUnits + (index < remainder ? 1 : 0), currencyCode)
  ))
}
